import type {
    PartnerCockpitContactDetail,
    PartnerCockpitLeadStatus,
    PartnerCockpitOfferRequestContact,
} from '@houzy/schemas/partner-cockpit/offer-requests';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { OfferRequestContactShortcut } from '../../../lib/trackingEvents';
import { FILL_HEIGHT_CARD_SX } from '../fillHeight';
import { offerRequestContactDetailsLockedHint } from '../labels';
import { contactShortcutOf } from '../offerRequestTracking';

export type OfferRequestContactCardProps = {
    contact: PartnerCockpitOfferRequestContact;
    leadStatus: PartnerCockpitLeadStatus;
    contactRestricted: boolean;
    isOnOldContract?: boolean;
    fillHeight?: boolean;
    onRevealContactDetail: (detail: PartnerCockpitContactDetail) => void;
    children: ReactNode;
};

type ContactDetailEntry = {
    detail: PartnerCockpitContactDetail;
    value: string | undefined;
    icon: ReactNode;
    actionLabel: string;
    revealLabel: string;
};

const CONTACT_DETAIL_URL_SCHEME: Record<PartnerCockpitContactDetail, string> = {
    phone: 'tel',
    email: 'mailto',
};

function initialsOf(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
}

function focusElement(element: HTMLElement | null): void {
    element?.focus();
}

const SHORTCUT_TOOLTIP_SX = {
    bgcolor: 'background.paper',
    color: 'text.primary',
    typography: 'body1',
    px: 2,
    py: 1.5,
    borderRadius: 1,
    boxShadow: 8,
} as const;

function ContactShortcut({
    entry,
    lockedHint,
    onReveal,
}: {
    entry: ContactDetailEntry;
    lockedHint?: string;
    onReveal: () => void;
}): ReactElement {
    if (entry.value === undefined) {
        const disabled = (
            <IconButton aria-label={entry.actionLabel} disabled>
                {entry.icon}
            </IconButton>
        );
        if (lockedHint === undefined) {
            return disabled;
        }
        return (
            <Tooltip title={lockedHint} slotProps={{ tooltip: { sx: SHORTCUT_TOOLTIP_SX } }}>
                <span>{disabled}</span>
            </Tooltip>
        );
    }
    return (
        <Tooltip title={entry.revealLabel}>
            <IconButton aria-label={entry.revealLabel} onClick={onReveal}>
                {entry.icon}
            </IconButton>
        </Tooltip>
    );
}

function RevealedContactDetail({
    entry,
    value,
    takesFocus,
    onClick,
}: {
    entry: ContactDetailEntry;
    value: string;
    takesFocus: boolean;
    onClick?: () => void;
}): ReactElement {
    return (
        <Link
            ref={takesFocus ? focusElement : undefined}
            onClick={onClick}
            href={`${CONTACT_DETAIL_URL_SCHEME[entry.detail]}:${value}`}
            aria-label={`${entry.actionLabel}: ${value}`}
            underline="hover"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0, fontSize: 18, mt: '1px' }}>
                {entry.icon}
            </Box>
            <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {value}
            </Box>
        </Link>
    );
}

export function OfferRequestContactHeader({
    contact,
    leadStatus,
    contactRestricted,
    isOnOldContract = false,
    onRevealContactDetail,
    onContactShortcut,
}: {
    contact: PartnerCockpitOfferRequestContact;
    leadStatus: PartnerCockpitLeadStatus;
    contactRestricted: boolean;
    isOnOldContract?: boolean;
    onRevealContactDetail: (detail: PartnerCockpitContactDetail) => void;
    onContactShortcut?: (kind: OfferRequestContactShortcut) => void;
}): ReactElement {
    const { t } = useTranslation();
    const [revealedByClick, setRevealedByClick] = useState<PartnerCockpitContactDetail>();
    const isReachable = contact.phone !== undefined || contact.email !== undefined;
    const showShortcuts = isReachable || !isOnOldContract;
    const lockedHint = isReachable ? undefined : offerRequestContactDetailsLockedHint(t, leadStatus, contactRestricted);

    const entries: ContactDetailEntry[] = [
        {
            detail: 'phone',
            value: contact.phone,
            icon: <PhoneOutlinedIcon fontSize="inherit" />,
            actionLabel: t('OFFER_REQUESTS.CONTACT.CALL'),
            revealLabel: t('OFFER_REQUESTS.CONTACT.SHOW_PHONE'),
        },
        {
            detail: 'email',
            value: contact.email,
            icon: <EmailOutlinedIcon fontSize="inherit" />,
            actionLabel: t('OFFER_REQUESTS.CONTACT.EMAIL'),
            revealLabel: t('OFFER_REQUESTS.CONTACT.SHOW_EMAIL'),
        },
    ];
    const isRevealed = (entry: ContactDetailEntry): boolean => contact.revealedDetails.includes(entry.detail);
    const shortcuts = showShortcuts ? entries.filter((entry) => entry.value === undefined || !isRevealed(entry)) : [];
    const revealed = entries.flatMap((entry) =>
        entry.value !== undefined && isRevealed(entry) ? [{ entry, value: entry.value }] : [],
    );

    function reveal(detail: PartnerCockpitContactDetail): void {
        setRevealedByClick(detail);
        onRevealContactDetail(detail);
    }

    const subheader =
        contact.address === undefined && revealed.length === 0 ? undefined : (
            <Stack spacing={0.5}>
                {contact.address !== undefined && <span>{contact.address}</span>}
                {revealed.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={0.5}>
                        {revealed.map(({ entry, value }) => (
                            <RevealedContactDetail
                                key={entry.detail}
                                entry={entry}
                                value={value}
                                takesFocus={entry.detail === revealedByClick}
                                {...(onContactShortcut
                                    ? { onClick: (): void => onContactShortcut(contactShortcutOf(entry.detail)) }
                                    : {})}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        );

    return (
        <CardHeader
            avatar={<Avatar aria-hidden>{initialsOf(contact.name)}</Avatar>}
            title={contact.name}
            subheader={subheader}
            slotProps={{
                title: { variant: 'subtitle1', component: 'h2' },
                subheader: { component: 'div' },
                content: { sx: { minWidth: 0 } },
            }}
            action={
                shortcuts.length > 0 ? (
                    <Stack direction="row" spacing={1}>
                        {shortcuts.map((entry) => (
                            <ContactShortcut
                                key={entry.detail}
                                entry={entry}
                                onReveal={() => reveal(entry.detail)}
                                {...(lockedHint === undefined ? {} : { lockedHint })}
                            />
                        ))}
                    </Stack>
                ) : undefined
            }
        />
    );
}

export function OfferRequestContactCard({
    contact,
    leadStatus,
    contactRestricted,
    isOnOldContract,
    fillHeight = false,
    onRevealContactDetail,
    children,
}: OfferRequestContactCardProps): ReactElement {
    return (
        <Card variant="outlined" sx={fillHeight ? FILL_HEIGHT_CARD_SX : undefined}>
            <OfferRequestContactHeader
                contact={contact}
                leadStatus={leadStatus}
                contactRestricted={contactRestricted}
                isOnOldContract={isOnOldContract}
                onRevealContactDetail={onRevealContactDetail}
            />
            <Divider />
            <Box sx={fillHeight ? { flex: 1, minHeight: 0, overflowY: 'auto' } : undefined}>{children}</Box>
        </Card>
    );
}
